import { FileDown } from 'lucide-react'
import { useEffect, useState } from 'react'
import { openDocument } from '@/lib/commands'
import { readFile } from '@/lib/files'
import { placeImage, prepareImageFile } from '@/lib/images'
import { useDoc } from '@/store/documentStore'

/** Drop a PDF anywhere to open it (or an image onto an open document to insert it). */
export function DropZone() {
  const [over, setOver] = useState(false)
  useEffect(() => {
    let depth = 0
    const hasFiles = (e: DragEvent) => e.dataTransfer?.types.includes('Files')
    const enter = (e: DragEvent) => {
      if (!hasFiles(e)) return
      depth++
      setOver(true)
    }
    const leave = () => {
      depth = Math.max(0, depth - 1)
      if (!depth) setOver(false)
    }
    const overFn = (e: DragEvent) => {
      if (hasFiles(e)) e.preventDefault()
    }
    const drop = async (e: DragEvent) => {
      if (!hasFiles(e)) return
      e.preventDefault()
      depth = 0
      setOver(false)
      const files = [...(e.dataTransfer?.files ?? [])]
      const pdf = files.find((f) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name))
      const img = files.find((f) => f.type.startsWith('image/'))
      if (pdf) {
        let handle: FileSystemFileHandle | null = null
        try {
          const item = [...(e.dataTransfer?.items ?? [])].find((i) => i.kind === 'file')
          const h = await (
            item as DataTransferItem & { getAsFileSystemHandle?: () => Promise<FileSystemHandle> }
          )?.getAsFileSystemHandle?.()
          if (h?.kind === 'file') handle = h as FileSystemFileHandle
        } catch {
          /* not supported */
        }
        await openDocument(await readFile(pdf), pdf.name, handle)
      } else if (img && useDoc.getState().status === 'ready') {
        placeImage(await prepareImageFile(img), 'image')
      }
    }
    window.addEventListener('dragenter', enter)
    window.addEventListener('dragleave', leave)
    window.addEventListener('dragover', overFn)
    window.addEventListener('drop', drop)
    return () => {
      window.removeEventListener('dragenter', enter)
      window.removeEventListener('dragleave', leave)
      window.removeEventListener('dragover', overFn)
      window.removeEventListener('drop', drop)
    }
  }, [])
  if (!over) return null
  return (
    <div className="pointer-events-none fixed inset-0 z-[95] flex animate-in items-center justify-center bg-accent/10 p-6 backdrop-blur-sm">
      <div className="flex h-full w-full flex-col items-center justify-center gap-3 rounded-[28px] border-[3px] border-accent border-dashed text-accent">
        <FileDown className="size-12" />
        <span className="font-semibold text-xl">Drop to open</span>
      </div>
    </div>
  )
}
